import React, { useCallback, useEffect, useMemo, useState } from 'react';
import API from '../../services/api';
import ToolBar from '../../components/toolbar';
import Table from '../../components/table';
import DropdownSearch from '../../components/dropdownSearch';
import EmptyState from '../../components/emptyState';
import Loading from '../../components/loading';
import columns from './tableHeader';
import CreateTargetSites from './createTargetSites';
import { buildSiteRows, filterAndSortSites } from '../../utils/storeAttribution';
import './styles.scss';

const ScreenToStoreContainer = ({ portal, lastUpdated }) => {
  const [createMode, setCreateMode] = useState(null);
  const [rows, setRows] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 15, sortBy: [] });

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    const loadCampaigns = async () => {
      if (!portal.companyId) return [];
      const result = [];
      const visited = new Set();
      let pageToken = '';
      do {
        if (visited.has(pageToken)) throw new Error('Repeated campaign page token');
        visited.add(pageToken);
        const response = await API.getCampaigns(
          portal.companyId, pageToken, encodeURI(`brandPortalId = "${portal.id}"`)
        );
        result.push(...(response.campaigns || []));
        pageToken = response.nextPageToken || '';
      } while (pageToken && active);
      return result;
    };

    Promise.all([API.getTargetSites(portal.id), API.getStoreAttributionConfigs(portal.id), loadCampaigns()])
      .then(([sitesResponse, configsResponse, campaignList]) => {
        if (!active) return;
        const nextRows = buildSiteRows(sitesResponse.sites, configsResponse.configurations, campaignList);
        const options = new Map(campaignList.map(campaign => [campaign.id, campaign.displayName || campaign.id]));
        nextRows.forEach(site => site.campaigns.forEach(campaign => options.set(campaign.id, campaign.displayName)));
        setRows(nextRows);
        setCampaigns([...options].map(([value, label]) => ({ value, label }))
          .sort((a, b) => a.label.localeCompare(b.label)));
        setCampaignId(current => options.has(current) ? current : '');
      })
      .catch(() => {
        if (active) setError('Target sites could not be loaded. Please try again.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [portal.id, portal.companyId, lastUpdated, retry]);

  const filtered = useMemo(() => filterAndSortSites(rows, search, campaignId, pagination.sortBy),
    [rows, search, campaignId, pagination.sortBy]);
  const fetchData = useCallback(next => setPagination(next), []);
  const options = useMemo(() => [{ value: '', label: 'All campaigns' }, ...campaigns], [campaigns]);
  const pageCount = Math.ceil(filtered.length / pagination.pageSize);
  const pageIndex = Math.min(pagination.pageIndex, Math.max(0, pageCount - 1));
  const data = filtered.slice(pageIndex * pagination.pageSize, (pageIndex + 1) * pagination.pageSize);

  if (createMode === 'single') {
    return (
      <div className="screen-to-store d-flex flex-column flex-fill">
        <CreateTargetSites portal={portal}
          onCancel={() => setCreateMode(null)}
          onCreated={() => {
            setCreateMode(null);
            setRetry(value => value + 1);
          }} />
      </div>
    );
  }

  return (
    <div className="screen-to-store d-flex flex-column flex-fill">
      <ToolBar
        title="Target Sites"
        icon="location"
        count={loading || error ? null : rows.length}
        onSearch={setSearch}
        searchingBy={search}
        searchPlaceholder="Search target site"
        hasButton
        buttonTitle="Create"
        buttonPress={() => setCreateMode('single')}
        renderFilters={
          <DropdownSearch
            label="Campaign"
            selected={options.find(option => option.value === campaignId)}
            options={options}
            onSelect={setCampaignId}
            disabled={loading || Boolean(error)}
            leftAlign
          />
        }
      />
      <div className="screen-to-store-content d-flex flex-column flex-fill">
        {loading ? (
          <div className="list-message" role="status"><Loading size={8} /><span>Loading target sites...</span></div>
        ) : error ? (
          <div className="list-message" role="alert">
            <p>{error}</p>
            <button className="btn btn-secondary" onClick={() => setRetry(value => value + 1)}>Try again</button>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="location" title={rows.length ? 'No matching target sites' : 'No target sites found'}
            details={rows.length ? 'Try another campaign or search term.' : 'This portal has no target sites yet.'} />
        ) : (
          <Table
            columns={columns}
            data={data}
            dataLabel="target sites"
            pageCount={pageCount}
            itemsCount={filtered.length}
            initialSortColumn="displayName"
            fetchData={fetchData}
            searchBy={search}
            filterBy={campaignId}
            resetOnChange={rows}
            isItemDetailsClosed
            onItemSelected={() => {}}
          />
        )}
      </div>
    </div>
  );
};

export default ScreenToStoreContainer;
